import { useDeferredValue, useEffect, useMemo, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  IconFilter,
  IconLayoutGrid,
  IconListDetails,
  IconPhoto,
  IconRefresh,
  IconSearch,
  IconUpload,
} from "@tabler/icons-react";
import { createBrowserRouter, RouterProvider } from "react-router-dom";
import type { MouseEvent as ReactMouseEvent } from "react";

import { installEasyAppBridge, resolveMountedBasename } from "@easy-core";
import {
  deleteMediaBulk,
  formatMediaDateTime,
  hasMediaStorageLimit,
  listMediaLibrary,
  MediaLibraryModal,
  type MediaImportResult,
  type MediaLibraryStats,
} from "@easy-media";
import { AppChrome, AppSecondaryBar, AppSurface } from "@easy-ui";

import "@easy-ui/legacy/appBase.css";
import "@easy-media/mediaLibrary.css";
import "./media.css";

type LibraryView = "list" | "grid";
type MediaUsageFilter = "all" | "used" | "unused";
type MediaSortOption = "newest" | "oldest" | "name" | "largest" | "most-used";

const MEDIA_SORT_LABELS: Record<MediaSortOption, string> = {
  newest: "Ajout recent",
  oldest: "Ajout ancien",
  name: "Nom A-Z",
  largest: "Poids decroissant",
  "most-used": "Usage le plus fort",
};

function openSelectFromFieldClick(event: ReactMouseEvent<HTMLLabelElement>) {
  if (event.target instanceof HTMLSelectElement) {
    return;
  }

  const select = event.currentTarget.querySelector("select");
  if (!select) {
    return;
  }

  select.focus();
  try {
    select.showPicker();
  } catch {
    // showPicker is unavailable in some browsers or blocked outside direct user activation.
  }
}

function StorageStatusBadge({ stats }: { stats?: MediaLibraryStats }) {
  const quota = stats?.quota;
  if (!quota || !hasMediaStorageLimit(quota) || (quota.status !== "warning" && quota.status !== "full")) {
    return null;
  }

  return (
    <span className={`media-storage-badge is-${quota.status}`} title={`${quota.used_label} utilises sur ${quota.limit_label || "-"}`}>
      Stockage {quota.usage_percent || 0}%
    </span>
  );
}

function SelectionCheckbox({
  checked,
  indeterminate = false,
  onChange,
  ariaLabel,
}: {
  checked: boolean;
  indeterminate?: boolean;
  onChange: (checked: boolean) => void;
  ariaLabel: string;
}) {
  const inputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.indeterminate = indeterminate;
    }
  }, [indeterminate]);

  return (
    <button
      type="button"
      className="table-checkbox-hitbox"
      aria-label={ariaLabel}
      onClick={(event) => {
        event.stopPropagation();
        onChange(!checked);
      }}
    >
      <input
        ref={inputRef}
        type="checkbox"
        className="table-checkbox"
        checked={checked}
        aria-label={ariaLabel}
        readOnly
        tabIndex={-1}
      />
    </button>
  );
}

function LibraryPage() {
  const queryClient = useQueryClient();
  const [view, setView] = useState<LibraryView>("list");
  const [isImportModalOpen, setIsImportModalOpen] = useState(false);
  const [selectedMediaId, setSelectedMediaId] = useState<number | null>(null);
  const [selectedMediaIds, setSelectedMediaIds] = useState<number[]>([]);
  const [statusMessage, setStatusMessage] = useState("");
  const [search, setSearch] = useState("");
  const [usageFilter, setUsageFilter] = useState<MediaUsageFilter>("all");
  const [sortOption, setSortOption] = useState<MediaSortOption>("newest");
  const [isFiltersPanelOpen, setIsFiltersPanelOpen] = useState(false);
  const deferredSearch = useDeferredValue(search);

  const mediaQuery = useQuery({
    queryKey: ["media-library-page"],
    queryFn: listMediaLibrary,
  });

  const bulkDeleteMutation = useMutation({
    mutationFn: async () => deleteMediaBulk(selectedMediaIds),
    onSuccess: async ({ deleted_count }) => {
      await queryClient.invalidateQueries({ queryKey: ["media-library-page"] });
      setSelectedMediaIds([]);
      setSelectedMediaId(null);
      setStatusMessage(`${deleted_count} média${deleted_count > 1 ? "s" : ""} supprimé${deleted_count > 1 ? "s" : ""}.`);
    },
    onError: (error) => {
      setStatusMessage(error instanceof Error ? error.message : "La suppression des médias a échoué.");
    },
  });

  const items = mediaQuery.data?.items || [];
  const stats = mediaQuery.data?.stats;
  const showMediaBlankState = !mediaQuery.isLoading && items.length === 0;
  const visibleItems = useMemo(() => {
    const normalizedSearch = deferredSearch.trim().toLowerCase();
    const filteredItems = items.filter((item) => {
      const matchesSearch =
        normalizedSearch.length === 0 ||
        [item.original_name, item.alt_text, item.mime_type, item.dimensions_label]
          .filter(Boolean)
          .some((value) => value.toLowerCase().includes(normalizedSearch));
      const matchesUsage =
        usageFilter === "all" ||
        (usageFilter === "used" ? item.usage_count > 0 : item.usage_count === 0);
      return matchesSearch && matchesUsage;
    });

    return [...filteredItems].sort((left, right) => {
      if (sortOption === "oldest") {
        return new Date(left.created_at).getTime() - new Date(right.created_at).getTime();
      }
      if (sortOption === "name") {
        return left.original_name.localeCompare(right.original_name, "fr", { sensitivity: "base" });
      }
      if (sortOption === "largest") {
        return right.size_bytes - left.size_bytes;
      }
      if (sortOption === "most-used") {
        return right.usage_count - left.usage_count || right.updated_at.localeCompare(left.updated_at);
      }
      return new Date(right.created_at).getTime() - new Date(left.created_at).getTime();
    });
  }, [deferredSearch, items, sortOption, usageFilter]);
  const activeFiltersCount = useMemo(
    () => [search.trim().length > 0, usageFilter !== "all"].filter(Boolean).length,
    [search, usageFilter]
  );
  const activeFilterChips = useMemo(() => {
    const chips: { key: "search" | "usage"; label: string }[] = [];
    if (search.trim()) {
      chips.push({ key: "search", label: `Recherche: ${search.trim()}` });
    }
    if (usageFilter === "used") {
      chips.push({ key: "usage", label: "Usage: Déjà utilisés" });
    }
    if (usageFilter === "unused") {
      chips.push({ key: "usage", label: "Usage: Jamais utilisés" });
    }
    return chips;
  }, [search, usageFilter]);
  const selectedMediaCount = selectedMediaIds.length;
  const visibleItemIds = useMemo(() => visibleItems.map((item) => item.id), [visibleItems]);
  const allItemsSelected =
    visibleItemIds.length > 0 && visibleItemIds.every((mediaId) => selectedMediaIds.includes(mediaId));
  const someItemsSelected = visibleItemIds.some((mediaId) => selectedMediaIds.includes(mediaId)) && !allItemsSelected;
  const selectedItem = useMemo(
    () => visibleItems.find((item) => item.id === selectedMediaId) || visibleItems[0] || null,
    [visibleItems, selectedMediaId]
  );

  useEffect(() => {
    document.title = "Relyo · Médias";
    installEasyAppBridge("media");
  }, []);

  useEffect(() => {
    setSelectedMediaIds((current) => current.filter((mediaId) => items.some((item) => item.id === mediaId)));
  }, [items]);

  useEffect(() => {
    if (visibleItems.length === 0) {
      setSelectedMediaId(null);
      return;
    }
    if (!selectedMediaId || !visibleItems.some((item) => item.id === selectedMediaId)) {
      setSelectedMediaId(visibleItems[0].id);
    }
  }, [visibleItems, selectedMediaId]);

  function toggleMediaSelection(mediaId: number, checked: boolean) {
    setSelectedMediaIds((current) => {
      if (checked) {
        return current.includes(mediaId) ? current : [...current, mediaId];
      }
      return current.filter((value) => value !== mediaId);
    });
    if (checked) {
      setSelectedMediaId(mediaId);
    }
  }

  function toggleSelectAllMedia(checked: boolean) {
    setSelectedMediaIds((current) => {
      if (checked) {
        return Array.from(new Set([...current, ...visibleItemIds]));
      }
      const visibleSet = new Set(visibleItemIds);
      return current.filter((mediaId) => !visibleSet.has(mediaId));
    });
    if (checked && visibleItems[0]) {
      setSelectedMediaId(visibleItems[0].id);
    }
  }

  function clearFilterChip(key: "search" | "usage") {
    if (key === "search") {
      setSearch("");
      return;
    }
    setUsageFilter("all");
  }

  return (
    <AppChrome
      className="media-page-shell"
      topBarStart={
        <div className="event-view-toggle" role="group" aria-label="Choix de la vue">
          <button
            type="button"
            className={`event-view-toggle-button${view === "list" ? " is-active" : ""}`}
            onClick={() => setView("list")}
          >
            <IconListDetails size="1rem" stroke={1.9} />
            Vue liste
          </button>
          <button
            type="button"
            className={`event-view-toggle-button${view === "grid" ? " is-active" : ""}`}
            onClick={() => setView("grid")}
          >
            <IconLayoutGrid size="1rem" stroke={1.9} />
            Vue grille
          </button>
        </div>
      }

    >
      <div className="media-library-shell">
        <div className="easy-media-page-layout">
          <AppSurface className="event-list-panel media-library-panel">
            <div className="app-catalog-toolbar">
              <div className="app-catalog-toolbar-main">
                <div className="app-catalog-search-shell media-catalog-search-shell">
                  <div className="app-catalog-searchbar">
                    <IconSearch className="ui-icon" size="1rem" stroke={1.9} />
                    <input
                      type="search"
                      value={search}
                      placeholder="Nom du fichier, format, texte alternatif..."
                      aria-label="Rechercher dans la médiathèque"
                      onChange={(event) => {
                        setSearch(event.currentTarget.value);
                      }}
                    />
                    {search.trim() ? (
                      <button
                        type="button"
                        className="app-catalog-clear-search"
                        aria-label="Effacer la recherche"
                        title="Effacer la recherche"
                        onClick={() => setSearch("")}
                      >
                        <span aria-hidden={true}>×</span>
                      </button>
                    ) : null}
                  </div>
                  <button
                    type="button"
                    className={`app-catalog-filter-toggle${activeFiltersCount > 0 ? " has-active-filters" : ""}${isFiltersPanelOpen ? " is-open" : ""}`}
                    aria-expanded={isFiltersPanelOpen}
                    aria-controls="media-filters-panel"
                    onClick={() => setIsFiltersPanelOpen((current) => !current)}
                  >
                    <IconFilter className="ui-icon" size="1rem" stroke={1.9} />
                    <span>Filtres</span>
                    {activeFiltersCount > 0 ? <span className="app-catalog-filter-count">{activeFiltersCount}</span> : null}
                  </button>
                </div>

                <div className="app-catalog-actions easy-media-page-actions">
                  <StorageStatusBadge stats={stats} />
                  <button type="button" className="button button-primary" onClick={() => setIsImportModalOpen(true)}>
                    <IconUpload size="1rem" stroke={1.9} />
                    Importer
                  </button>
                </div>
              </div>

              {isFiltersPanelOpen ? (
                <div id="media-filters-panel" className="app-catalog-advanced-filters">
                  <div className="app-catalog-advanced-grid is-two-columns">
                    <label className="app-catalog-advanced-field" onClick={openSelectFromFieldClick}>
                      <span className="app-catalog-field-label">Usage</span>
                      <div className="toolbar-dropdown">
                        <select
                          className="toolbar-select"
                          value={usageFilter}
                          onChange={(event) => {
                            const value = event.currentTarget.value as MediaUsageFilter;
                            setUsageFilter(value);
                          }}
                        >
                          <option value="all">Tous les médias</option>
                          <option value="used">Déjà utilisés</option>
                          <option value="unused">Jamais utilisés</option>
                        </select>
                      </div>
                    </label>

                    <label className="app-catalog-advanced-field" onClick={openSelectFromFieldClick}>
                      <span className="app-catalog-field-label">Tri</span>
                      <div className="toolbar-dropdown">
                        <select
                          className="toolbar-select"
                          value={sortOption}
                          onChange={(event) => {
                            const value = event.currentTarget.value as MediaSortOption;
                            setSortOption(value);
                          }}
                        >
                          {Object.entries(MEDIA_SORT_LABELS).map(([value, label]) => (
                            <option key={value} value={value}>
                              {label}
                            </option>
                          ))}
                        </select>
                      </div>
                    </label>
                  </div>

                  <div className="app-catalog-advanced-footer is-actions-only">
                    <button
                      type="button"
                      className="button app-catalog-secondary-button"
                      onClick={() => {
                        setSearch("");
                        setUsageFilter("all");
                        setSortOption("newest");
                      }}
                      disabled={activeFiltersCount === 0 && sortOption === "newest"}
                    >
                      Réinitialiser
                    </button>
                  </div>
                </div>
              ) : null}

              <div className="app-catalog-meta">
                <div className="app-catalog-meta-main">
                  <strong>
                    {visibleItems.length} média{visibleItems.length > 1 ? "s" : ""}
                  </strong>
                  <span className="app-catalog-meta-detail">Tri: {MEDIA_SORT_LABELS[sortOption]}</span>
                  {activeFiltersCount > 0 ? (
                    <span className="app-catalog-meta-detail">
                      {activeFiltersCount} filtre{activeFiltersCount > 1 ? "s" : ""} actif{activeFiltersCount > 1 ? "s" : ""}
                    </span>
                  ) : null}
                </div>
                <button
                  type="button"
                  className="app-catalog-refresh"
                  aria-label="Rafraîchir la médiathèque"
                  title="Rafraîchir la médiathèque"
                  onClick={() => mediaQuery.refetch()}
                >
                  <IconRefresh size="1rem" stroke={1.9} />
                </button>
              </div>

              {activeFilterChips.length > 0 ? (
                <div className="app-active-filter-chips" aria-label="Filtres actifs">
                  {activeFilterChips.map((chip) => (
                    <button
                      key={chip.key}
                      type="button"
                      className="app-active-filter-chip"
                      onClick={() => clearFilterChip(chip.key)}
                    >
                      <span>{chip.label}</span>
                      <span aria-hidden={true}>×</span>
                    </button>
                  ))}
                </div>
              ) : null}
            </div>

            {view === "list" && selectedMediaCount > 0 ? (
              <AppSecondaryBar className="media-selection-bar">
                <button
                  type="button"
                  className="button"
                  disabled={selectedMediaCount === 0}
                  onClick={() => setSelectedMediaIds([])}
                >
                  Effacer la sélection
                </button>
                <button
                  type="button"
                  className="button button-danger"
                  disabled={selectedMediaCount === 0 || bulkDeleteMutation.isPending}
                  onClick={() => {
                    if (selectedMediaCount === 0) {
                      return;
                    }
                    const confirmed = window.confirm(
                      selectedMediaCount === 1
                        ? "Supprimer ce média ?"
                        : `Supprimer ${selectedMediaCount} médias ?`
                    );
                    if (!confirmed) {
                      return;
                    }
                    bulkDeleteMutation.mutate();
                  }}
                >
                  {bulkDeleteMutation.isPending ? "Suppression..." : "Supprimer"}
                </button>
                <span className="muted media-selection-hint">{selectedMediaCount} média sélectionné{selectedMediaCount > 1 ? "s" : ""}.</span>
              </AppSecondaryBar>
            ) : null}

            {statusMessage ? <p className="status-line">{statusMessage}</p> : null}
            {mediaQuery.error instanceof Error ? <p className="status-line error">{mediaQuery.error.message}</p> : null}

            {mediaQuery.isLoading ? (
              <div className="easy-media-empty">Chargement de la médiathèque...</div>
            ) : showMediaBlankState ? (
              <div className="app-empty-state media-module-empty-state">
                <div className="app-empty-state-visual">
                  <IconPhoto size={36} stroke={1.9} aria-hidden={true} />
                </div>
                <div className="app-empty-state-copy">
                  <h3>Ajoutez vos premiers visuels</h3>
                  <p>Importez une image dans la bibliothèque partagée pour l'utiliser ensuite dans les évènements et les newsletters.</p>
                </div>
                <div className="app-empty-state-actions">
                  <button type="button" className="button button-primary" onClick={() => setIsImportModalOpen(true)}>
                    <IconUpload size={18} stroke={1.9} aria-hidden={true} />
                    <span>Importer un visuel</span>
                  </button>
                </div>
              </div>
            ) : visibleItems.length === 0 ? (
              <div className="easy-media-empty">
                <strong>Aucun média ne correspond</strong>
                <p>Essayez une recherche plus large ou réinitialisez les filtres.</p>
              </div>
            ) : view === "list" ? (
              <div className="easy-media-table-wrap">
                <table className="easy-media-table">
                  <thead>
                    <tr>
                      <th className="table-select-col">
                        <SelectionCheckbox
                          checked={allItemsSelected}
                          indeterminate={someItemsSelected}
                          ariaLabel="Sélectionner tous les médias"
                          onChange={toggleSelectAllMedia}
                        />
                      </th>
                          <th>Aperçu</th>
                      <th>Fichier</th>
                      <th>Poids</th>
                      <th>Date d'ajout</th>
                          <th>Dernière utilisation</th>
                      <th>Usage</th>
                    </tr>
                  </thead>
                  <tbody>
                    {visibleItems.map((item) => (
                      <tr
                        key={item.id}
                        className={[
                          selectedItem?.id === item.id ? "is-selected" : "",
                          selectedMediaIds.includes(item.id) ? "row-bulk-selected" : "",
                        ]
                          .filter(Boolean)
                          .join(" ")}
                        tabIndex={0}
                        onClick={() => setSelectedMediaId(item.id)}
                        onKeyDown={(event) => {
                          if (event.key === "Enter" || event.key === " ") {
                            event.preventDefault();
                            setSelectedMediaId(item.id);
                          }
                        }}
                      >
                        <td className="table-select-col">
                          <SelectionCheckbox
                            checked={selectedMediaIds.includes(item.id)}
                            ariaLabel={`Sélectionner ${item.original_name}`}
                            onChange={(checked) => toggleMediaSelection(item.id, checked)}
                          />
                        </td>
                        <td>
                          <div className="easy-media-table-preview">
                            <img src={item.public_url} alt={item.alt_text || item.original_name} />
                          </div>
                        </td>
                        <td className="easy-media-table-name">
                          <strong>{item.original_name}</strong>
                          <div className="easy-media-table-subtext">{item.dimensions_label || item.mime_type}</div>
                        </td>
                        <td>{item.size_label}</td>
                        <td>{formatMediaDateTime(item.created_at)}</td>
                        <td>{formatMediaDateTime(item.last_used_at)}</td>
                        <td>{item.usage_count} utilisation(s)</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="easy-media-page-grid">
                {visibleItems.map((item) => (
                  <button key={item.id} type="button" className="easy-media-page-grid-card" onClick={() => setSelectedMediaId(item.id)}>
                    <div className="easy-media-page-preview">
                      <img src={item.public_url} alt={item.alt_text || item.original_name} />
                    </div>
                    <div className="easy-media-page-grid-copy">
                      <strong>{item.original_name}</strong>
                      <span>{item.size_label}</span>
                      <span>{formatMediaDateTime(item.created_at)}</span>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </AppSurface>

          <section className="panel panel-primary media-detail-panel">
            <div className="panel-head">
              <div>
                <p className="section-kicker">Sélection</p>
                <h2>{selectedItem ? selectedItem.original_name : "Aucun média"}</h2>
                <p className="muted">Le panneau de droite reprend la logique détail des autres apps du workspace.</p>
              </div>
            </div>
            <div className="easy-media-inspector-preview">
              {selectedItem ? <img src={selectedItem.public_url} alt={selectedItem.alt_text || selectedItem.original_name} /> : null}
            </div>
            <div className="easy-media-meta-list">
              <div>
                <span>Nom</span>
                <strong>{selectedItem?.original_name || "-"}</strong>
              </div>
              <div>
                <span>Date d'ajout</span>
                <strong>{formatMediaDateTime(selectedItem?.created_at)}</strong>
              </div>
              <div>
                <span>Taille</span>
                <strong>{selectedItem?.size_label || "-"}</strong>
              </div>
              <div>
                <span>Dimensions</span>
                <strong>{selectedItem?.dimensions_label || "-"}</strong>
              </div>
              <div>
                <span>Dernière utilisation</span>
                <strong>{formatMediaDateTime(selectedItem?.last_used_at)}</strong>
              </div>
              <div>
                <span>URL publique</span>
                <strong>{selectedItem?.public_url || "-"}</strong>
              </div>
            </div>
          </section>
        </div>
      </div>

      <MediaLibraryModal
        opened={isImportModalOpen}
        mode="import-only"
        title="Importer un visuel"
        subtitle="Ajoutez une image à la médiathèque partagée. La bibliothèque reste visible sur cette page."
        onClose={() => setIsImportModalOpen(false)}
        onImportComplete={async (result: MediaImportResult) => {
          setStatusMessage(result.message);
          await queryClient.invalidateQueries({ queryKey: ["media-library-page"] });
          setSelectedMediaIds([]);
          setSelectedMediaId(result.item.id);
        }}
      />

    </AppChrome>
  );
}

export function createMediaRoutes(listPath = "/") {
  return [
    {
      path: listPath,
      element: <LibraryPage />,
    },
  ];
}

export function createHostedMediaRoutes() {
  return createMediaRoutes("/media");
}

const router = createBrowserRouter(createMediaRoutes(), {
  basename: resolveMountedBasename("media"),
});

export function App() {
  return <RouterProvider router={router} />;
}
